(() => {
  if (customElements.get('ohs-build-set')) return;

  class OhsBuildSet extends HTMLElement {
    connectedCallback() {
      this.checkboxes = Array.from(this.querySelectorAll('[data-ohs-set-item]'));
      this.button = this.querySelector('[data-ohs-add-set]');
      this.total = this.querySelector('[data-ohs-set-total]');
      this.count = this.querySelector('[data-ohs-selected-count]');
      this.error = this.querySelector('[data-ohs-set-error]');
      this.productInfo = this.closest('.product__info-container');
      this.quantityInput = this.productInfo?.querySelector('input[name="quantity"]') || document.querySelector('input[name="quantity"]');
      this.currentItem = this.querySelector('[data-ohs-current-item]');
      this.currentPrice = this.querySelector('[data-ohs-current-price]');
      this.currentMeta = this.querySelector('[data-ohs-current-meta]');
      this.currentImage =
        this.querySelector('.ohs-build-set__item--current .ohs-build-set__image') ||
        this.querySelector('[data-ohs-current-image]');
      this.currentVariantImages = {};
      const currentVariantImagesJson = this.querySelector('[data-ohs-current-variant-images]')?.textContent;
      if (currentVariantImagesJson) {
        try {
          this.currentVariantImages = JSON.parse(currentVariantImagesJson);
        } catch (error) {
          console.error('OHS Build Set: unable to read current variant image map.', error);
        }
      }
      this.variantSelects = Array.from(this.querySelectorAll('[data-ohs-variant-select]'));
      this.packQuantityInputs = Array.from(this.querySelectorAll('[data-ohs-pack-quantity]'));

      this.checkboxes.forEach((checkbox) => checkbox.addEventListener('change', () => this.updateSummary()));
      this.variantSelects.forEach((select) => select.addEventListener('change', () => this.onConfigurableVariantChange(select)));
      this.packQuantityInputs.forEach((input) => {
        input.addEventListener('input', () => this.onPackQuantityChange(input));
        input.addEventListener('change', () => this.onPackQuantityChange(input));
      });
      this.quantityInput?.addEventListener('change', () => this.onMainQuantityChange());
      this.quantityInput?.addEventListener('input', () => this.onMainQuantityChange());
      this.button?.addEventListener('click', () => this.addSelected());

      this.onProductFormVariantChange = (event) => {
        if (event.target?.name !== 'id') return;
        this.syncFromSelectedVariant();
      };
      this.productInfo?.addEventListener('change', this.onProductFormVariantChange);

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
        this.variantUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => this.onMainVariantChange(event));
      }

      this.syncVariantSelectsToMain();
      this.syncPackQuantities();
      this.updateSummary();
    }

    disconnectedCallback() {
      if (typeof this.variantUnsubscriber === 'function') this.variantUnsubscriber();
      if (this.onProductFormVariantChange) {
        this.productInfo?.removeEventListener('change', this.onProductFormVariantChange);
      }
    }

    syncCurrentVariant(variant) {
      if (!variant || !this.currentItem) return;

      this.currentItem.dataset.variantId = variant.id;
      this.currentItem.dataset.priceCents = variant.price;

      if (this.currentPrice) this.currentPrice.textContent = this.money(Number(variant.price || 0));
      if (this.currentMeta && variant.title) this.currentMeta.textContent = String(variant.title).split(' / ')[0];
      this.updateCurrentImage(variant);

      this.updateCurrentSyncValue(variant);
      this.syncVariantSelectsToMain();
      this.syncPackQuantities();
      this.updateSummary();
    }

    updateCurrentImage(variant) {
      if (!this.currentImage || !variant?.id) return;
      const variantId = String(variant.id).split('/').pop();
      const mappedSrc = this.currentVariantImages[String(variant.id)] || this.currentVariantImages[variantId] || '';
      const eventSrc = variant?.featured_media?.preview_image?.src || variant?.featured_image?.src || variant?.image?.src || '';
      const src = mappedSrc || eventSrc;
      if (!src) return;
      this.currentImage.src = src;
      this.currentImage.removeAttribute('srcset');
    }

    updateCurrentSyncValue(variant) {
      const index = parseInt(this.dataset.currentSyncIndex || '', 10);
      if (!Number.isInteger(index) || !variant?.options) return;
      this.dataset.currentSyncValue = variant.options[index] || '';
    }

    syncVariantSelectsToMain() {
      const syncValue = this.dataset.currentSyncValue;
      if (!syncValue) return;

      this.variantSelects.forEach((select) => {
        const match = Array.from(select.options).find((option) => option.dataset.syncValue === syncValue);
        if (!match) return;
        select.value = match.value;
        this.onConfigurableVariantChange(select);
      });
    }

    syncFromSelectedVariant() {
      const selectedVariantJson = this.productInfo?.querySelector('variant-selects [data-selected-variant]')?.textContent;
      if (!selectedVariantJson) return;

      try {
        this.syncCurrentVariant(JSON.parse(selectedVariantJson));
      } catch (error) {
        console.error('OHS Build Set: unable to read selected variant.', error);
      }
    }

    onMainVariantChange(event) {
      this.syncCurrentVariant(event?.data?.variant);
    }

    onConfigurableVariantChange(select) {
      const card = select.closest('[data-ohs-configurable-item]');
      const checkbox = card?.querySelector('[data-ohs-set-item]');
      const price = card?.querySelector('[data-ohs-config-price]');
      const image = card?.querySelector('.ohs-build-set__image');
      if (!checkbox || !price) return;

      const option = select.selectedOptions[0];
      if (!option?.value) {
        checkbox.checked = false;
        checkbox.disabled = true;
        checkbox.dataset.variantId = '';
        checkbox.dataset.priceCents = '0';
        price.textContent = '—';
        if (image && option?.dataset?.fallbackImageUrl) {
          image.src = option.dataset.fallbackImageUrl;
          image.removeAttribute('srcset');
        }
      } else {
        checkbox.disabled = false;
        checkbox.dataset.variantId = option.value;
        checkbox.dataset.priceCents = option.dataset.priceCents || '0';
        price.textContent = this.money(Number(option.dataset.priceCents || 0));
        if (image) {
          const imageUrl = option.dataset.imageUrl || option.dataset.fallbackImageUrl || '';
          if (imageUrl) {
            image.src = imageUrl;
            image.removeAttribute('srcset');
          }
        }
      }
      this.updateSummary();
    }

    mainQuantity() {
      return Math.max(1, parseInt(this.quantityInput?.value || '1', 10) || 1);
    }

    currentPackSize() {
      return Math.max(1, parseInt(this.currentItem?.dataset.packSize || '1', 10) || 1);
    }

    coverageUnits() {
      return this.mainQuantity() * this.currentPackSize();
    }

    onMainQuantityChange() {
      this.syncPackQuantities();
      this.updateSummary();
    }

    onPackQuantityChange(input) {
      const value = Math.max(1, parseInt(input.value || '1', 10) || 1);
      input.value = value;
      input.dataset.userAdjusted = 'true';
      this.updateSummary();
    }

    syncPackQuantities(force = false) {
      const coverage = this.coverageUnits();
      this.packQuantityInputs.forEach((input) => {
        if (!force && input.dataset.userAdjusted === 'true') return;
        const row = input.closest('.ohs-build-set__item');
        const checkbox = row?.querySelector('[data-ohs-set-item]');
        const packSize = Math.max(1, parseInt(checkbox?.dataset.packSize || '1', 10) || 1);
        input.value = Math.max(1, Math.ceil(coverage / packSize));
      });
    }

    itemQuantity(checkbox) {
      if (checkbox === this.currentItem) return this.mainQuantity();

      const packSize = Math.max(1, parseInt(checkbox.dataset.packSize || '1', 10) || 1);
      const row = checkbox.closest('.ohs-build-set__item');
      const packInput = row?.querySelector('[data-ohs-pack-quantity]');

      if (packInput) {
        return Math.max(1, parseInt(packInput.value || '1', 10) || 1);
      }

      return Math.max(1, Math.ceil(this.coverageUnits() / packSize));
    }

    selectedItems() {
      return this.checkboxes
        .filter((checkbox) => checkbox.checked && checkbox.dataset.variantId)
        .map((checkbox) => ({
          id: Number(String(checkbox.dataset.variantId).split('/').pop()),
          quantity: this.itemQuantity(checkbox),
          priceCents: Number(checkbox.dataset.priceCents || 0),
        }));
    }

    money(cents) {
      const currency = this.dataset.currency || 'ZAR';
      try {
        return new Intl.NumberFormat('en-ZA', { style: 'currency', currency }).format(cents / 100);
      } catch (e) {
        return 'R' + (cents / 100).toFixed(2);
      }
    }

    updateSummary() {
      const items = this.selectedItems();
      const totalCents = items.reduce((sum, item) => sum + item.priceCents * item.quantity, 0);
      if (this.count) this.count.textContent = 'Total for ' + items.length + (items.length === 1 ? ' selected item' : ' selected items');
      if (this.total) this.total.textContent = this.money(totalCents);
      if (this.button) this.button.textContent = 'Add all ' + items.length + (items.length === 1 ? ' item' : ' items') + ' to cart';
    }

    async addSelected() {
      const items = this.selectedItems();
      if (!items.length || !this.button) return;

      this.error.hidden = true;
      this.error.textContent = '';
      this.button.setAttribute('aria-disabled', 'true');
      this.button.textContent = 'Adding…';

      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

      try {
        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        config.headers['Content-Type'] = 'application/json';

        const body = { items: items.map(({ id, quantity }) => ({ id, quantity })) };

        if (cart?.getSectionsToRender) {
          body.sections = cart.getSectionsToRender().map((section) => section.id);
          body.sections_url = window.location.pathname;
          if (cart.setActiveElement) cart.setActiveElement(document.activeElement);
        }

        config.body = JSON.stringify(body);
        const response = await fetch(routes.cart_add_url, config);
        const data = await response.json();

        if (!response.ok || data.status) {
          throw new Error(data.description || data.message || 'One or more selected items could not be added.');
        }

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'ohs-build-set',
            productVariantId: items[0].id,
            cartData: data,
          });
        }

        if (cart?.renderContents) {
          if (
            cart.tagName?.toLowerCase() === 'cart-notification' &&
            !data.key &&
            Array.isArray(data.items) &&
            data.items.length
          ) {
            data.key = data.items[0].key;
          }

          if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
          cart.renderContents(data);
        } else {
          window.location = window.routes?.cart_url || '/cart';
        }
      } catch (error) {
        this.error.textContent = error.message || 'We could not add the selected items. Please try again.';
        this.error.hidden = false;
      } finally {
        this.button.removeAttribute('aria-disabled');
        this.updateSummary();
      }
    }
  }

  customElements.define('ohs-build-set', OhsBuildSet);

  document.querySelectorAll('[data-ohs-build-set]').forEach((element) => {
    if (element.tagName.toLowerCase() !== 'ohs-build-set') {
      const replacement = document.createElement('ohs-build-set');
      Array.from(element.attributes).forEach((attr) => replacement.setAttribute(attr.name, attr.value));
      replacement.innerHTML = element.innerHTML;
      element.replaceWith(replacement);
    }
  });
})();
